<template>
  <div>
    <spinner-loader :loadingStuff="loadingStuff" />
    <app-template />
    <div class="uk-container uk-container-large book-physio-container">
      <!-- Header -->
      <div class="page-header">
        <h2><i class="bi bi-calendar-plus"></i> Book Physio Appointment</h2>
        <p class="subtitle">Select a physio and choose an available time slot</p>
      </div>

      <!-- Physio Selection -->
      <div class="physio-selection-card">
        <h5><i class="bi bi-person-badge"></i> Select Physio</h5>
        <div class="physio-grid" v-if="physios.length > 0">
          <div v-for="physio in physios" :key="physio.id"
            :class="['physio-card', { selected: selectedPhysioId === physio.id }]" @click="selectPhysio(physio.id)">
            <img v-if="physio.userImage" :src="getImgUrl(physio.userImage)" class="physio-avatar" />
            <div v-else class="physio-avatar-placeholder">
              <i class="bi bi-person"></i>
            </div>
            <span class="physio-name">{{ physio.name }}</span>
          </div>
        </div>
        <div v-else class="no-physios">
          <p>No physios available at this time.</p>
        </div>
      </div>

      <!-- Week Navigation -->
      <div v-if="selectedPhysioId" class="week-navigation">
        <button class="btn btn-outline-primary" @click="previousWeek">
          <i class="bi bi-chevron-left"></i> Previous
        </button>
        <div class="current-week">
          <i class="bi bi-calendar3"></i>
          {{ formatWeekRange }}
        </div>
        <button class="btn btn-outline-primary" @click="nextWeek">
          Next <i class="bi bi-chevron-right"></i>
        </button>
        <button class="btn btn-link" @click="goToToday">Today</button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-border text-primary" role="status"></div>
        <p>Loading availability...</p>
      </div>

      <!-- Available Slots -->
      <div v-else-if="selectedPhysioId" class="availability-section">
        <h5><i class="bi bi-clock"></i> Available Slots</h5>
        <div class="days-grid">
          <div v-for="day in weekDays" :key="day.date" class="day-column"
            :class="{ 'is-today': isToday(day.date), 'is-past': isPast(day.date), 'no-clinic': !hasClinic(day.date) }">
            <div class="day-header">
              <span class="day-name">{{ day.dayName }}</span>
              <span class="day-date">{{ day.displayDate }}</span>
              <span v-if="hasClinic(day.date)" class="clinic-hours">{{ clinicHoursForDay(day.date) }}</span>
              <span v-else class="clinic-hours closed">No clinic</span>
            </div>
            <div class="slots-list">
              <!-- "No clinic" (day not opened by the physio) is greyed out so
                   nobody turns up on a day there's no one there; "Fully
                   booked" is a clinic day with every slot taken. -->
              <div v-if="getAvailableSlotsForDay(day.date).length === 0" class="no-slots">
                <span>{{ hasClinic(day.date) ? "Fully booked" : "—" }}</span>
              </div>
              <button v-for="slot in getAvailableSlotsForDay(day.date)" :key="slot.id" class="slot-btn"
                :disabled="isPast(day.date)" @click="openBookingModal(day.date, slot)">
                {{ formatTime(slot.start_time) }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Prompt to select physio -->
      <div v-else class="select-prompt">
        <i class="bi bi-hand-index"></i>
        <p>Please select a physio above to view their availability</p>
      </div>

      <!-- My Upcoming Appointments -->
      <div v-if="myAppointments.length > 0" class="my-appointments">
        <h5><i class="bi bi-calendar-check"></i> My Upcoming Appointments</h5>
        <div class="appointments-list">
          <div v-for="apt in myAppointments" :key="apt.id" class="appointment-item">
            <div class="apt-info">
              <strong>{{ formatDateFull(apt.appointment_date) }}</strong>
              <span class="apt-time">{{ formatTime(apt.start_time) }} - {{ formatTime(apt.end_time) }}</span>
              <span class="apt-physio">with {{ apt.physio_name }}</span>
              <span v-if="apt.reason" class="apt-reason">{{ apt.reason }}</span>
            </div>
            <div class="apt-actions">
              <button class="btn btn-sm btn-outline-primary" @click="openEditModal(apt)">
                <i class="bi bi-pencil"></i> Edit
              </button>
              <button class="btn btn-sm btn-outline-danger" @click="cancelAppointment(apt)">
                <i class="bi bi-x"></i> Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Booking Modal -->
      <div v-if="showBookingModal" class="modal-overlay">
        <div class="modal-dialog booking-modal">
          <div class="modal-header">
            <h4>Confirm Appointment</h4>
            <button class="close-btn" @click="closeBookingModal">&times;</button>
          </div>
          <div class="modal-body">
            <div class="booking-summary">
              <div class="summary-item">
                <i class="bi bi-calendar"></i>
                <span>{{ formatDateFull(selectedSlot?.date) }}</span>
              </div>
              <div class="summary-item">
                <i class="bi bi-clock"></i>
                <span>{{ formatTime(selectedSlot?.start_time) }} - {{ formatTime(selectedSlot?.end_time) }}</span>
              </div>
              <div class="summary-item">
                <i class="bi bi-person-badge"></i>
                <span>{{ selectedPhysioName }}</span>
              </div>
            </div>
            <div class="form-group">
              <label>Reason for appointment</label>
              <select v-model="bookingForm.reason" class="form-select">
                <option value="">Select reason...</option>
                <option value="Follow-up">Follow-up</option>
                <option value="Assessment">Assessment</option>
                <option value="Treatment">Treatment</option>
                <option value="Rehabilitation">Rehabilitation</option>
                <option value="Return to play assessment">Return to play assessment</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div class="form-group">
              <label>Notes (optional)</label>
              <textarea v-model="bookingForm.notes" class="form-control" rows="3"
                placeholder="Any additional information..."></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeBookingModal">Cancel</button>
            <button class="btn btn-success" @click="confirmBooking" :disabled="bookingLoading">
              <span v-if="bookingLoading"><span class="spinner-border spinner-border-sm"></span></span>
              <span v-else><i class="bi bi-check-circle"></i> Book Appointment</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Edit Appointment Modal -->
      <div v-if="showEditModal" class="modal-overlay" @mousedown="handleEditOverlayClick"
        style="isolation: isolate;">
        <div class="modal-dialog edit-modal" @mousedown.stop
          style="position: relative; z-index: 1; pointer-events: auto;">
          <div class="modal-header">
            <h4><i class="bi bi-pencil-square"></i> Edit Appointment</h4>
            <button class="close-btn" @click="closeEditModal">&times;</button>
          </div>
          <div class="modal-body">
            <!-- Current Appointment Info -->
            <div class="current-appointment-info">
              <h6>Current Appointment</h6>
              <div class="info-row">
                <i class="bi bi-calendar"></i>
                <span>{{ formatDateFull(editingAppointment?.appointment_date) }}</span>
              </div>
              <div class="info-row">
                <i class="bi bi-clock"></i>
                <span>{{ formatTime(editingAppointment?.start_time) }} - {{ formatTime(editingAppointment?.end_time) }}</span>
              </div>
              <div class="info-row">
                <i class="bi bi-person-badge"></i>
                <span>{{ editingAppointment?.physio_name }}</span>
              </div>
            </div>

            <!-- Reschedule Section -->
            <div class="reschedule-section">
              <div class="form-check mb-3">
                <input type="checkbox" class="form-check-input" id="wantReschedule"
                  v-model="editForm.wantReschedule">
                <label class="form-check-label" for="wantReschedule">
                  <strong>Reschedule to a different time</strong>
                </label>
              </div>

              <div v-if="editForm.wantReschedule" class="reschedule-options">
                <div class="form-group">
                  <label>Select New Date</label>
                  <input type="date" class="form-control" v-model="editForm.newDate"
                    :min="minRescheduleDate"
                    @change="onRescheduleDateChange">
                </div>

                <div v-if="editForm.newDate && rescheduleAvailability.length > 0" class="form-group">
                  <label>Available Time Slots</label>
                  <div class="time-slots-grid">
                    <button v-for="slot in rescheduleAvailability" :key="slot.start_time"
                      type="button"
                      :class="['time-slot-btn', { selected: editForm.newStartTime === slot.start_time }]"
                      @click="selectRescheduleSlot(slot)">
                      {{ formatTime(slot.start_time) }}
                    </button>
                  </div>
                </div>

                <div v-else-if="editForm.newDate && !loadingRescheduleSlots" class="no-slots-message">
                  <i class="bi bi-exclamation-circle"></i>
                  No available slots for this date. Please select another date.
                </div>

                <div v-if="loadingRescheduleSlots" class="loading-slots">
                  <span class="spinner-border spinner-border-sm"></span> Loading availability...
                </div>
              </div>
            </div>

            <!-- Reason and Notes -->
            <div class="form-group">
              <label>Reason for appointment</label>
              <select v-model="editForm.reason" class="form-select">
                <option value="">Select reason...</option>
                <option value="Follow-up">Follow-up</option>
                <option value="Assessment">Assessment</option>
                <option value="Treatment">Treatment</option>
                <option value="Rehabilitation">Rehabilitation</option>
                <option value="Return to play assessment">Return to play assessment</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div class="form-group">
              <label>Notes</label>
              <textarea v-model="editForm.notes" class="form-control" rows="3"
                placeholder="Any additional information..."></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeEditModal">Cancel</button>
            <button class="btn btn-primary" @click="saveEditedAppointment" :disabled="editLoading || !canSaveEdit">
              <span v-if="editLoading"><span class="spinner-border spinner-border-sm"></span></span>
              <span v-else><i class="bi bi-check-circle"></i> Save Changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppTemplate from "../layouts/App";
import SpinnerLoader from "../../components/LoadingSpinning";
import { PhysioCalendarService } from "@/services/physio-calendar-service.js";
import { mapGetters } from "vuex";
import dayjs from "dayjs";

export default {
  name: "BookPhysio",
  components: {
    AppTemplate,
    SpinnerLoader,
  },
  data() {
    return {
      loadingStuff: false,
      loading: false,
      userData: {},
      physios: [],
      selectedPhysioId: null,
      weekStart: dayjs().startOf('week'),
      availability: [],
      appointments: [],
      myAppointments: [],
      // Booking modal
      showBookingModal: false,
      selectedSlot: null,
      bookingForm: {
        reason: '',
        notes: ''
      },
      bookingLoading: false,
      // Edit modal
      showEditModal: false,
      editingAppointment: null,
      editForm: {
        wantReschedule: false,
        newDate: '',
        newStartTime: '',
        newEndTime: '',
        reason: '',
        notes: ''
      },
      editLoading: false,
      loadingRescheduleSlots: false,
      rescheduleAvailability: []
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    selectedPhysioName() {
      const physio = this.physios.find(p => p.id === this.selectedPhysioId);
      return physio?.name || 'Unknown';
    },
    weekDays() {
      const days = [];
      for (let i = 0; i < 7; i++) {
        const date = this.weekStart.add(i, 'day');
        days.push({
          date: date.format('YYYY-MM-DD'),
          dayName: date.format('ddd'),
          displayDate: date.format('D MMM')
        });
      }
      return days;
    },
    formatWeekRange() {
      const end = this.weekStart.add(6, 'day');
      return `${this.weekStart.format('D MMM')} - ${end.format('D MMM YYYY')}`;
    },
    minRescheduleDate() {
      return dayjs().format('YYYY-MM-DD');
    },
    canSaveEdit() {
      // Can save if there are changes to reason/notes, or valid reschedule
      if (this.editForm.wantReschedule) {
        return this.editForm.newDate && this.editForm.newStartTime;
      }
      // Allow save if reason or notes changed
      const hasReasonChange = this.editForm.reason !== (this.editingAppointment?.reason || '');
      const hasNotesChange = this.editForm.notes !== (this.editingAppointment?.notes || '');
      return hasReasonChange || hasNotesChange;
    }
  },
  async mounted() {
    if (this.getProfile) {
      this.userData = { ...this.getProfile };
      await this.loadPhysios();
      await this.loadMyAppointments();
    }
  },
  watch: {
    getProfile: {
      handler(newVal) {
        if (newVal && !this.userData?.id) {
          this.userData = { ...newVal };
          this.loadPhysios();
          this.loadMyAppointments();
        }
      },
      immediate: true
    },
    weekStart() {
      if (this.selectedPhysioId) {
        this.loadAvailability();
      }
    },
    'editForm.newDate'(newDate) {
      if (newDate && this.editingAppointment) {
        this.loadRescheduleAvailability(newDate);
      } else {
        this.rescheduleAvailability = [];
      }
    }
  },
  methods: {
    getImgUrl(img) {
      if (!img) return '';
      if (img.startsWith('http')) return img;
      return `/images/${img}`;
    },

    async loadPhysios() {
      if (!this.userData.clubId) return;
      try {
        const response = await PhysioCalendarService.getAllPhysios(this.userData.clubId);
        this.physios = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error loading physios:', error);
      }
    },

    async selectPhysio(physioId) {
      this.selectedPhysioId = physioId;
      await this.loadAvailability();
    },

    async loadAvailability() {
      if (!this.selectedPhysioId) return;
      this.loading = true;
      try {
        const weekStartStr = this.weekStart.format('YYYY-MM-DD');
        const weekEndStr = this.weekStart.add(6, 'day').format('YYYY-MM-DD');

        const [availResponse, apptResponse] = await Promise.all([
          PhysioCalendarService.getAvailability({
            physio_id: this.selectedPhysioId,
            week_start: weekStartStr,
            week_end: weekEndStr
          }),
          PhysioCalendarService.getAppointments({
            physio_id: this.selectedPhysioId,
            week_start: weekStartStr,
            week_end: weekEndStr
          })
        ]);

        this.availability = Array.isArray(availResponse.data) ? availResponse.data : [];
        this.appointments = Array.isArray(apptResponse.data) ? apptResponse.data : [];
      } catch (error) {
        console.error('Error loading availability:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadMyAppointments() {
      if (!this.userData.id) return;
      try {
        const response = await PhysioCalendarService.getPlayerAppointments({
          player_id: this.userData.id,
          upcoming_only: true
        });
        this.myAppointments = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error loading appointments:', error);
      }
    },

    getAvailableSlotsForDay(date) {
      // Get availability slots for this day that aren't booked
      const dayAvailability = this.availability.filter(a => a.date === date);
      const dayAppointments = this.appointments.filter(a => a.appointment_date === date);

      // Helper to normalize time format (remove seconds if present)
      const normalizeTime = (time) => {
        if (!time) return '';
        // Handle "HH:MM:SS" or "HH:MM" format
        return time.substring(0, 5);
      };

      return dayAvailability.filter(slot => {
        const slotTime = normalizeTime(slot.start_time);
        return !dayAppointments.some(apt =>
          normalizeTime(apt.start_time) === slotTime
        );
      });
    },

    // A clinic day = the physio opened at least one slot on it (booked or not).
    hasClinic(date) {
      return this.availability.some(a => a.date === date);
    },

    // "17:30 – 19:00": earliest slot start to latest slot end that day.
    clinicHoursForDay(date) {
      const slots = this.availability.filter(a => a.date === date);
      if (!slots.length) return '';
      const starts = slots.map(s => (s.start_time || '').substring(0, 5)).filter(Boolean).sort();
      const ends = slots.map(s => (s.end_time || '').substring(0, 5)).filter(Boolean).sort();
      if (!starts.length) return '';
      const last = ends.length ? ends[ends.length - 1] : starts[starts.length - 1];
      return `${this.formatTime(starts[0])} – ${this.formatTime(last)}`;
    },

    isToday(dateStr) {
      return dayjs(dateStr).isSame(dayjs(), 'day');
    },

    isPast(dateStr) {
      return dayjs(dateStr).isBefore(dayjs(), 'day');
    },

    previousWeek() {
      this.weekStart = this.weekStart.subtract(7, 'day');
    },

    nextWeek() {
      this.weekStart = this.weekStart.add(7, 'day');
    },

    goToToday() {
      this.weekStart = dayjs().startOf('week');
    },

    formatTime(time) {
      return PhysioCalendarService.formatTime(time);
    },

    formatDateFull(dateStr) {
      if (!dateStr) return '';
      return dayjs(dateStr).format('dddd, D MMMM YYYY');
    },

    openBookingModal(date, slot) {
      this.selectedSlot = { date, ...slot };
      this.bookingForm = { reason: '', notes: '' };
      this.showBookingModal = true;
    },

    closeBookingModal() {
      this.showBookingModal = false;
      this.selectedSlot = null;
    },

    handleOverlayClick(event) {
      // Only close if clicking directly on the overlay, not on any child elements
      if (event.target.classList.contains('modal-overlay')) {
        this.closeBookingModal();
      }
    },

    async confirmBooking() {
      if (!this.selectedSlot) return;

      this.bookingLoading = true;
      try {
        const response = await PhysioCalendarService.bookSlot({
          physio_id: this.selectedPhysioId,
          player_id: this.userData.id,
          date: this.selectedSlot.date,
          start_time: this.selectedSlot.start_time,
          end_time: this.selectedSlot.end_time,
          reason: this.bookingForm.reason,
          notes: this.bookingForm.notes
        });

        if (response.data?.success) {
          this.$toast?.success('Appointment booked successfully!');
          this.closeBookingModal();
          await this.loadAvailability();
          await this.loadMyAppointments();
        } else {
          this.$toast?.error(response.data?.message || 'Failed to book appointment');
        }
      } catch (error) {
        console.error('Error booking appointment:', error);
        this.$toast?.error('Error booking appointment');
      } finally {
        this.bookingLoading = false;
      }
    },

    async cancelAppointment(appointment) {
      if (!confirm('Are you sure you want to cancel this appointment?')) return;

      this.loadingStuff = true;
      try {
        const response = await PhysioCalendarService.cancelAppointment({
          appointment_id: appointment.id,
          user_id: this.userData.id,
          user_type: this.userData.userType
        });

        if (response.data?.success) {
          this.$toast?.success('Appointment cancelled');
          await this.loadMyAppointments();
          if (this.selectedPhysioId) {
            await this.loadAvailability();
          }
        } else {
          this.$toast?.error(response.data?.message || 'Failed to cancel appointment');
        }
      } catch (error) {
        console.error('Error cancelling appointment:', error);
        this.$toast?.error('Error cancelling appointment');
      } finally {
        this.loadingStuff = false;
      }
    },

    // Edit Appointment Methods
    openEditModal(appointment) {
      this.editingAppointment = { ...appointment };
      this.editForm = {
        wantReschedule: false,
        newDate: '',
        newStartTime: '',
        newEndTime: '',
        reason: appointment.reason || '',
        notes: appointment.notes || ''
      };
      this.rescheduleAvailability = [];
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editingAppointment = null;
      this.rescheduleAvailability = [];
    },

    handleEditOverlayClick(event) {
      // Only close if clicking directly on the overlay background
      if (event.target === event.currentTarget) {
        this.closeEditModal();
      }
    },

    async loadRescheduleAvailability(date) {
      if (!this.editingAppointment?.physio_id) {
        return;
      }

      this.loadingRescheduleSlots = true;
      this.rescheduleAvailability = [];
      this.editForm.newStartTime = '';
      this.editForm.newEndTime = '';

      try {
        // Load availability and appointments for the selected date
        const [availResponse, apptResponse] = await Promise.all([
          PhysioCalendarService.getAvailability({
            physio_id: this.editingAppointment.physio_id,
            week_start: date,
            week_end: date
          }),
          PhysioCalendarService.getAppointments({
            physio_id: this.editingAppointment.physio_id,
            week_start: date,
            week_end: date
          })
        ]);

        const dayAvailability = Array.isArray(availResponse.data) ? availResponse.data : [];
        const dayAppointments = Array.isArray(apptResponse.data) ? apptResponse.data : [];

        // Helper to normalize time format
        const normalizeTime = (time) => {
          if (!time) return '';
          return time.substring(0, 5);
        };

        // Filter out booked slots (except the current appointment)
        this.rescheduleAvailability = dayAvailability.filter(slot => {
          const slotTime = normalizeTime(slot.start_time);
          return !dayAppointments.some(apt =>
            apt.id !== this.editingAppointment.id &&
            normalizeTime(apt.start_time) === slotTime
          );
        });
      } catch (error) {
        console.error('Error loading reschedule availability:', error);
      } finally {
        this.loadingRescheduleSlots = false;
      }
    },

    selectRescheduleSlot(slot) {
      this.editForm.newStartTime = slot.start_time;
      this.editForm.newEndTime = slot.end_time;
    },

    onRescheduleDateChange() {
      const newDate = this.editForm.newDate;
      if (newDate && this.editingAppointment) {
        this.loadRescheduleAvailability(newDate);
      } else {
        this.rescheduleAvailability = [];
      }
    },

    async saveEditedAppointment() {
      if (!this.editingAppointment || !this.canSaveEdit) return;

      this.editLoading = true;
      try {
        const updateData = {
          appointment_id: this.editingAppointment.id,
          reason: this.editForm.reason,
          notes: this.editForm.notes
        };

        // Add reschedule data if user wants to reschedule
        if (this.editForm.wantReschedule && this.editForm.newDate && this.editForm.newStartTime) {
          updateData.new_date = this.editForm.newDate;
          updateData.new_start_time = this.editForm.newStartTime;
          updateData.new_end_time = this.editForm.newEndTime;
        }

        const response = await PhysioCalendarService.updateAppointment(updateData);

        if (response.data?.success) {
          const message = this.editForm.wantReschedule
            ? 'Appointment rescheduled successfully!'
            : 'Appointment updated successfully!';
          this.$toast?.success(message);
          this.closeEditModal();
          await this.loadMyAppointments();
          if (this.selectedPhysioId) {
            await this.loadAvailability();
          }
        } else {
          this.$toast?.error(response.data?.message || 'Failed to update appointment');
        }
      } catch (error) {
        console.error('Error updating appointment:', error);
        this.$toast?.error('Error updating appointment');
      } finally {
        this.editLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.book-physio-container {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 24px;

  h2 {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;

    i {
      color: #28a745;
    }
  }

  .subtitle {
    color: #6c757d;
    margin: 0;
  }
}

.physio-selection-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  h5 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;

    i {
      color: #0d6efd;
    }
  }
}

.physio-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.physio-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;

  &:hover {
    border-color: #0d6efd;
    background: #f8f9fa;
  }

  &.selected {
    border-color: #0d6efd;
    background: #e7f3ff;
  }

  .physio-avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 8px;
  }

  .physio-avatar-placeholder {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: #e9ecef;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;

    i {
      font-size: 24px;
      color: #6c757d;
    }
  }

  .physio-name {
    font-weight: 500;
    text-align: center;
  }
}

.week-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  .current-week {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    font-size: 1.1em;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #6c757d;

  p {
    margin-top: 12px;
  }
}

.select-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 12px;
  text-align: center;

  i {
    font-size: 3rem;
    margin-bottom: 16px;
  }
}

.availability-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;

  h5 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;

    i {
      color: #28a745;
    }
  }
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.day-column {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;

  &.is-today {
    border-color: #0d6efd;
    box-shadow: 0 0 0 1px #0d6efd;
  }

  &.is-past {
    opacity: 0.5;
  }

  &.no-clinic {
    background: #eceff1;
    border-style: dashed;
    opacity: 0.6;

    .day-header {
      background: #e3e7ea;
    }
  }

  .day-header {
    background: #f8f9fa;
    padding: 12px;
    text-align: center;
    border-bottom: 1px solid #e9ecef;

    .day-name {
      display: block;
      font-weight: 600;
    }

    .day-date {
      font-size: 0.85em;
    }

    .clinic-hours {
      display: block;
      margin-top: 4px;
      font-size: 0.75em;
      font-weight: 600;
      color: #155724;

      &.closed {
        color: #6c757d;
        font-weight: 500;
      }
    }
  }

  .slots-list {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 100px;
  }

  .no-slots {
    color: #6c757d;
    font-size: 0.85em;
    text-align: center;
    padding: 20px 0;
  }

  .slot-btn {
    background: #d4edda;
    border: 1px solid #28a745;
    color: #155724;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    font-weight: 500;

    &:hover:not(:disabled) {
      background: #28a745;
      color: white;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.my-appointments {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  h5 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;

    i {
      color: #17a2b8;
    }
  }
}

.appointments-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.appointment-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #17a2b8;
  flex-wrap: wrap;
  gap: 12px;

  .apt-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 200px;

    .apt-time {
      color: #0d6efd;
      font-weight: 500;
    }

    .apt-physio {
      color: #6c757d;
      font-size: 0.9em;
    }

    .apt-reason {
      font-size: 0.85em;
      color: #6c757d;
      font-style: italic;
    }
  }

  .apt-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  pointer-events: auto;
}

.modal-overlay > .modal-dialog {
  pointer-events: auto;
  position: relative;
  z-index: 10000;
}

.modal-dialog {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  margin: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  z-index: 10000;
  position: relative;
  overflow: visible;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  position: relative;
  z-index: 10002;

  h4 {
    margin: 0;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #6c757d;
    pointer-events: auto !important;
    position: relative;
    z-index: 10003;
    padding: 4px 8px;

    &:hover {
      color: #000;
    }
  }
}

.modal-body {
  padding: 20px;
  position: relative;
  z-index: 10001;

  .booking-summary {
    background: #f8f9fa;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 20px;

    .summary-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 0;

      i {
        color: #0d6efd;
        width: 20px;
      }
    }
  }

  .form-group {
    margin-bottom: 16px;
    position: relative;
    z-index: 10002;

    label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
    }

    .form-select,
    .form-control,
    select,
    textarea,
    input {
      pointer-events: auto !important;
      position: relative;
      z-index: 10003;
      background-color: #fff;
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  position: relative;
  z-index: 10001;

  .btn {
    pointer-events: auto !important;
    position: relative;
    z-index: 10002;
  }
}

/* Edit Modal Styles */
.edit-modal {
  max-width: 550px;
}

.current-appointment-info {
  background: #e7f3ff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  border-left: 4px solid #0d6efd;

  h6 {
    margin: 0 0 12px 0;
    color: #0d6efd;
    font-weight: 600;
  }

  .info-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 0;

    i {
      color: #0d6efd;
      width: 18px;
    }
  }
}

.reschedule-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;

  .form-check-label {
    cursor: pointer;
  }

  .reschedule-options {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;
  }
}

.time-slots-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.time-slot-btn {
  background: #e9ecef;
  border: 2px solid transparent;
  color: #495057;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    background: #dee2e6;
    border-color: #0d6efd;
  }

  &.selected {
    background: #0d6efd;
    color: white;
    border-color: #0d6efd;
  }
}

.no-slots-message {
  color: #dc3545;
  font-size: 0.9em;
  padding: 12px;
  background: #f8d7da;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;

  i {
    font-size: 1.1em;
  }
}

.loading-slots {
  color: #6c757d;
  font-size: 0.9em;
  padding: 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
